import { LinearProgress } from "@mui/material";
import classNames from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectAvailableLanguages } from "../../app/slices/TrainingLocalizationSlice";
import { AssetKey, AssetVariantDTO } from "../../dto/AssetDTO";
import { ReactComponent as FileIcon } from "../file_explorer/assets/file.svg";
import { variantFileName, variantKind, variantLanguage } from "./variantDisplay";

interface VariantTileProps {
  variant: AssetVariantDTO;
  assetKey: AssetKey;
  /** Target for the search field's aria-activedescendant. */
  optionId?: string;
  isSelected: boolean;
  onSelect: () => void;
}

/**
 * One localization as a card. Deliberately the same class list as the file
 * explorer's `AssetCard` and the prop library's tile so all three grids read
 * as one system — including the selected treatment.
 *
 * The grid exists for images, where a localization is the same artwork
 * redrawn and seeing them side by side is the point. For audio, PDF and video
 * every tile is the same glyph, which is why the list stays the default.
 */
export default function VariantTile(props: VariantTileProps) {
  const { variant, assetKey, optionId, isSelected, onSelect } = props;
  const { t } = useTranslation();
  const languages = useSelector(selectAvailableLanguages);
  const [failed, setFailed] = useState(false);

  const kind = variantKind(variant, languages);
  const fileName = variantFileName(variant);
  const isLoading = kind === "loading";

  const title = isLoading
    ? t("localize_ui.uploading")
    : kind === "default"
      ? t("default")
      : kind === "assigned"
        ? (variantLanguage(variant, languages)?.full ?? "")
        : (fileName ?? t("localize_ui.new_localization"));

  const showImage =
    assetKey === AssetKey.Image && variant.downloadUri && !failed;

  const tintClass =
    assetKey === AssetKey.Pdf
      ? "assetcard-pdf"
      : assetKey === AssetKey.Video
        ? "assetcard-video"
        : assetKey === AssetKey.Audio
          ? "assetcard-audio"
          : "assetcard-loading-icon";

  return (
    <div
      id={optionId}
      role="option"
      aria-selected={isSelected}
      tabIndex={-1}
      onClick={() => !isLoading && onSelect()}
      data-testid={`variant-tile-${variant.id}`}
      className={classNames(
        "assetcard-root group w-full h-[172px] rounded-[8px] border flex flex-col overflow-hidden text-left select-none transition-colors",
        {
          "border-creator-primary bg-creator-primary-washed": isSelected,
          "border-creator-border bg-creator-surface hover:border-[var(--pi-menu-text-color-1-faded)]":
            !isSelected,
          "cursor-pointer": !isLoading,
          "cursor-progress": isLoading,
        }
      )}
    >
      <div className="h-[104px] w-full shrink-0 bg-interaction-softBackground flex items-center justify-center overflow-hidden">
        {showImage ? (
          <img
            src={variant.downloadUri}
            alt=""
            loading="lazy"
            // contain, not cover: the grid exists so localized artwork can be
            // compared, and a crop hides exactly what you came to look at.
            className="pointer-events-none select-none object-contain w-full h-full"
            onError={() => setFailed(true)}
          />
        ) : (
          <FileIcon className={tintClass + " w-[34px] h-[34px]"} />
        )}
      </div>

      <div className="flex-1 min-h-0 px-[10px] py-[8px] flex flex-col gap-[6px]">
        <div className="flex items-center gap-[8px]">
          <div className="flex-1 min-w-0 text-[12.5px] leading-[18px] text-creator-text truncate">
            {title}
          </div>
          {kind === "default" && (
            <span className="shrink-0 text-[10.5px] text-creator-text-soft" aria-hidden>
              —
            </span>
          )}
          {kind === "assigned" && (
            <span className="shrink-0 text-[10.5px] uppercase text-creator-text-sub">
              {variant.variant}
            </span>
          )}
        </div>

        <div className="flex items-center gap-[6px] min-h-[16px]">
          {isLoading ? (
            <span className="flex-1">
              <LinearProgress />
            </span>
          ) : kind === "unassigned" ? (
            <>
              {/* Amber carries the state; the label stays readable text. */}
              <span
                aria-hidden
                className="w-[6px] h-[6px] shrink-0 rounded-full bg-creator-notice"
              />
              <span className="text-[10.5px] text-creator-notice-text truncate">
                {t("localize_ui.not_set")}
              </span>
            </>
          ) : (
            <span className="flex-1 min-w-0 truncate text-[10.5px] leading-[16px] text-creator-text-sub">
              {kind === "default" ? t("localize_ui.default_description") : fileName}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
